import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { messages } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';

type BulkResult = { requested: number; applied: number; skipped: number };

const reportResult = (result: BulkResult) => {
  toast.success(`${result.applied.toLocaleString('ja-JP')} 件に反映しました`, {
    description:
      result.skipped > 0 ? `${result.skipped.toLocaleString('ja-JP')} 件は対象外でした` : undefined,
  });
};

/** 担当の一括割当（manager 以上） */
export const BulkAssignDialog = ({
  open,
  onOpenChange,
  companyIds,
  assignees,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyIds: string[];
  assignees: { id: string; name: string | null; email: string }[];
  onDone: () => void;
}) => {
  const [assigneeId, setAssigneeId] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<BulkResult>>('/companies/bulk/assign', {
        companyIds,
        assigneeId: assigneeId || null,
      }),
    onSuccess: (result) => {
      reportResult(result.data);
      onOpenChange(false);
      onDone();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>担当を割り当てる</DialogTitle>
          <DialogDescription>
            {messages.common.countCompanies(companyIds.length)}の担当を変更します。
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label="担当者">
            {(props) => (
              <Select {...props} value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                <option value="">{messages.common.unassigned}</option>
                {assignees.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name ?? user.email}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button size="lg" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            割り当てる
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** タグの一括付与・削除 */
export const BulkTagDialog = ({
  open,
  onOpenChange,
  companyIds,
  tags,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyIds: string[];
  tags: { id: string; name: string }[];
  onDone: () => void;
}) => {
  const [addIds, setAddIds] = useState<Set<string>>(new Set());
  const [removeIds, setRemoveIds] = useState<Set<string>>(new Set());

  const mutation = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<BulkResult>>('/companies/bulk/tags', {
        companyIds,
        addTagIds: [...addIds],
        removeTagIds: [...removeIds],
      }),
    onSuccess: (result) => {
      reportResult(result.data);
      onOpenChange(false);
      setAddIds(new Set());
      setRemoveIds(new Set());
      onDone();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const toggle = (set: Set<string>, setter: (next: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>タグを変更する</DialogTitle>
          <DialogDescription>
            {messages.common.countCompanies(companyIds.length)}のタグを変更します。
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <fieldset className="space-y-1">
            <legend className="mb-1 text-body font-semibold">付与するタグ</legend>
            {tags.length === 0 && <p className="text-body text-foreground-muted">タグがありません</p>}
            {tags.map((tag) => (
              <label key={tag.id} className="flex min-h-touch items-center gap-2 text-body">
                <input
                  type="checkbox"
                  className="size-4 accent-foreground"
                  checked={addIds.has(tag.id)}
                  onChange={() => toggle(addIds, setAddIds, tag.id)}
                />
                {tag.name}
              </label>
            ))}
          </fieldset>
          <fieldset className="space-y-1">
            <legend className="mb-1 text-body font-semibold">削除するタグ</legend>
            {tags.map((tag) => (
              <label key={tag.id} className="flex min-h-touch items-center gap-2 text-body">
                <input
                  type="checkbox"
                  className="size-4 accent-foreground"
                  checked={removeIds.has(tag.id)}
                  onChange={() => toggle(removeIds, setRemoveIds, tag.id)}
                />
                {tag.name}
              </label>
            ))}
          </fieldset>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            size="lg"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || (addIds.size === 0 && removeIds.size === 0)}
          >
            {messages.common.apply}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 一括の対象外設定（理由必須） */
export const BulkExcludeDialog = ({
  open,
  onOpenChange,
  companyIds,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyIds: string[];
  onDone: () => void;
}) => {
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<BulkResult>>('/companies/bulk/exclude', {
        companyIds,
        reason,
        exclude: true,
      }),
    onSuccess: (result) => {
      reportResult(result.data);
      onOpenChange(false);
      setReason('');
      onDone();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>対象外にする</DialogTitle>
          <DialogDescription>
            {messages.common.countCompanies(companyIds.length)}を送信対象から外します。
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label={messages.common.reason} required>
            {(props) => (
              <Input
                {...props}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="既存顧客のため"
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            size="lg"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !reason.trim()}
            disabledReason={!reason.trim() ? messages.company.excludeReasonRequired : undefined}
          >
            {messages.company.exclude}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
